import { LocalDate } from "@js-joda/core";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import {
  CaregiverImportedVisitDetails,
  CaregiverVisitEditRequest,
  ClockedInStatus,
  ClockInOutCallType,
  ImportedVisitId,
  Month,
  PatientTaskInstance,
  PatientTaskInstanceId,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
  VisitInstanceId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { translate } from "../../translate";
import { KeyedList, List } from "../../utils/List";
import { CicoModalEntity, getClosestVisit } from "../../utils/visitUtils";
import { AssignedVisitsAndTasksList } from "../AssignedVisitsAndTasksList";
import MonthPicker from "../MonthPicker/MonthPicker";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import { UseContactSupportReturnType } from "../useContactSupport";

type Props = {
  caregiver: AppCaregiver;
  importedVisits: List<CaregiverImportedVisitDetails>;
  clockedInStatus: ClockedInStatus;
  contactSupportProps: UseContactSupportReturnType;
  patientTasks: List<PatientTaskInstance>;
  rescheduleRequests: List<CaregiverVisitEditRequest>;
  scheduleYear: number;
  scheduleMonth: Month;
  visitInstances: KeyedList<VisitInstanceDetails, "id">;
  onClickGoBack: () => void;
  onClickStartNow: (entity: CicoModalEntity) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onRequestAvailableVisits: () => void;
  onRequestNavigate: (page: Page) => void;
  onUploadVerificationSelfie: (params: {
    visitInstanceId: VisitInstanceId;
    imageBase64: string;
    clockType: ClockInOutCallType;
  }) => Promise<void>;
  onClickPreviousMonth: () => void;
  onClickNextMonth: () => void;
  onChangeYearMonth: (year: number, month: number) => void;
  onRequestVisitAdjustmentCheck: API["getCaregiverCanAdjustVisitInstanceTimes"];
  onCheckIfDocumentsSubmitted: API["checkIfAllDocumentsSubmitted"];
  onCaregiverMarkVisitAsMissed: API["caregiverMarkVisitAsMissed"];
};

export default function ScheduledVisitsScreen(props: Props) {
  const handleClickVisitInstance = (visitInstanceId: VisitInstanceId) => {
    return props.onRequestNavigate({ type: "VisitInstance", visitInstanceId });
  };

  const handleClickImportedVisit = (importedVisitId: ImportedVisitId, patientName: string) => {
    props.onRequestNavigate({
      type: "ConvertImportedVisit",
      importedVisitId: importedVisitId,
      patientName: patientName,
    });
  };

  const handleClickTask = (
    patientTaskInstanceId: PatientTaskInstanceId,
    initialScheduleDateTimePickerOpen: boolean
  ) => {
    props.onRequestNavigate({
      type: "PatientTaskInstance",
      patientTaskInstanceId: patientTaskInstanceId,
      initialScheduleDateTimePickerOpen: initialScheduleDateTimePickerOpen,
    });
  };

  React.useEffect(() => {
    const closestVisit = getClosestVisit(props.visitInstances);
    const date = closestVisit?.data.startTimeLocal.toLocalDate() ?? LocalDate.now();
    const elem = document.getElementById("day" + date.toString());
    const timeout = window.setTimeout(() => elem?.scrollIntoView({ behavior: "smooth" }), 150);

    return () => window.clearTimeout(timeout);
  }, [props.visitInstances]);

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.MY_VISITS)} />}
      extraHeader={
        <MonthPicker
          year={props.scheduleYear}
          month={props.scheduleMonth}
          onClickPrevious={props.onClickPreviousMonth}
          onClickNext={props.onClickNextMonth}
          onChange={props.onChangeYearMonth}
        />
      }
      onClickGoBack={props.onClickGoBack}
    >
      <AssignedVisitsAndTasksList
        caregiverId={props.caregiver.id}
        isCaregiverOfMedflyt={props.caregiver.isCaregiverOfMedFlyt}
        importedVisits={props.importedVisits}
        clockedInStatus={props.clockedInStatus}
        contactSupportProps={props.contactSupportProps}
        patientTasksInstances={props.patientTasks}
        rescheduleRequests={props.rescheduleRequests}
        scheduleView="DATE"
        scheduleViewMonth={props.scheduleMonth}
        scheduleViewYear={props.scheduleYear}
        visitInstances={props.visitInstances}
        onClickStartNow={props.onClickStartNow}
        onClickTask={handleClickTask}
        onClock={props.onClock}
        onImportedVisitClick={handleClickImportedVisit}
        onRequestAvailableVisits={props.onRequestAvailableVisits}
        onRequestNavigate={props.onRequestNavigate}
        onScheduledVisitClick={handleClickVisitInstance}
        onUploadVerificationSelfie={(visitInstanceId, imageBase64, clockType) =>
          props.onUploadVerificationSelfie({ visitInstanceId, imageBase64, clockType })
        }
        onRequestVisitAdjustmentCheck={props.onRequestVisitAdjustmentCheck}
        onClickNextMonth={props.onClickNextMonth}
        onCheckIfDocumentsSubmitted={props.onCheckIfDocumentsSubmitted}
        onCaregiverMarkVisitAsMissed={props.onCaregiverMarkVisitAsMissed}
      />
      <div style={{ height: 64 }} />
    </Scaffold>
  );
}
